<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { isEmpty } from 'lodash'
import { Columns3 } from 'lucide-vue-next'
import { useExtendedI18n } from '@/i18n'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import EntitiesCardTable from '@/modules/investing/components/entities/entities-card-table.vue'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import LifecycleBadge from '@/modules/investing/components/lifecycle-badge.vue'
import LifecyclePill from '@/modules/investing/components/lifecycle-pill.vue'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { useHiddenMetrics } from '@/modules/workspace/composables/use-hidden-metrics'
import { VBadge, VButton, VDataTable, VDropdownImproved, VIcon, VSearchInput } from '@/modules/shared/components'
import { export_csv } from '@/modules/shared/utils/export-csv'
import { useIndividualEntityColumns } from '@/modules/investing/config/entity-columns'
import { useEntityColumnVisibility } from '@/modules/investing/composables/use-entity-column-visibility'
import { useEntityListFilters } from '@/modules/investing/composables/use-entity-list-filters'
import { useLifecycleFilter } from '@/modules/investing/composables/use-lifecycle-filter'
import { moicLabel, pctCalled, pctDeployed, pctOfGoal } from '@/modules/investing/utils/entity-row-formatters'

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const route = useRoute()
const { individual_id, slug } = route.params as { individual_id: string; slug: string }
const { n } = useExtendedI18n()
const authStore = useAuthStore()
const individualStore = useIndividualStore()
const { hidden } = useHiddenMetrics()
const individual = computed(() => individualStore.individual)

const funds = computed(() => individualStore.listEntities.filter((entity) => entity.entity_type === 'fund'))

const isAdmin = computed(() => authStore.is_site_or_group_admin)
const is_viewing_own_profile = computed(() => individual_id === authStore.current_user.investor_id?.toString())
const is_entity_link_disabled = computed(() => !isAdmin.value && !is_viewing_own_profile.value)

const isEntityAdmin = (entity: any): boolean => {
  const adminIds = (entity.admins ?? []).map((a: any) => a.id)
  return adminIds.includes(parseInt(individual_id))
}

///////////////////////////////////////////////////////////////////////////////
// Columns — shared with the SPVs individual page; see entity-columns.ts.
///////////////////////////////////////////////////////////////////////////////

const baseColumns = useIndividualEntityColumns({ entityType: 'fund', hidden })

///////////////////////////////////////////////////////////////////////////////
// Column visibility + filters — see the shared composables.
///////////////////////////////////////////////////////////////////////////////

const { visibleColumnKeys, columns, columnOptions, gridTemplateColumns } = useEntityColumnVisibility(baseColumns)
const { lifecycleFilter, lifecyclePills, statusCounts, applyLifecycleFilter } = useLifecycleFilter(funds, {
  entityType: 'fund',
})

const lifecycleFiltered = computed(() => applyLifecycleFilter(funds.value))
const filters = useEntityListFilters(lifecycleFiltered)

const filteredFunds = computed(() =>
  filters.filteredItems.value.map((item: any) => ({
    ...item,
    accent_color:
      item.lifecycle_state === 'raising' ? '#059669' : item.lifecycle_state === 'closed' ? '#3092b3' : undefined,
  })),
)

///////////////////////////////////////////////////////////////////////////////
// Fetch
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetch = async () => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug })
  // Default to surfacing every shareholder this individual represents (self +
  // managed entities) since the page no longer offers a "Filter by profiles"
  // dropdown to narrow the set.
  const shareholder_cids = individualStore.listShareholders.map((s: any) => `${s.type}:${s.id}`)
  await individualStore.fetchEntities(individual.value.entity_ids, { slug, shareholder_cids })
  hideSkeleton()
}

const onExport = () => {
  const filename = `${slug}-individual-${individual_id}-funds`
  const exportColumns = columns.value.filter((c: any) => !c.hidden)
  export_csv(filename, filteredFunds.value, exportColumns, n)
}

onMounted(async () => {
  await fetch()
})
</script>

<template>
  <IndividualLayout selectedTab="funds">
    <EntitiesCardTable :gridTemplateColumns="gridTemplateColumns">
      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <div class="flex flex-wrap items-center gap-2">
          <LifecyclePill
            v-for="pill in lifecyclePills"
            :key="pill.key"
            :filter-key="pill.key"
            :label="pill.label"
            :count="statusCounts[pill.key]"
            :active="lifecycleFilter === pill.key"
            @select="lifecycleFilter = pill.key"
          />
        </div>
        <div class="flex items-center gap-2">
          <VSearchInput v-model="filters.searchFilter.value" placeholder="Search Funds..." />
          <VButton size="md" variant="secondary" @click="onExport">
            <div class="flex items-center justify-center space-x-2">
              <VIcon name="Download" class="h-4 w-4" />
              <span>Export</span>
            </div>
          </VButton>
          <VDropdownImproved v-model="visibleColumnKeys" :options="columnOptions" aligned="right" :hideDisabled="true">
            <button type="button" class="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 hover:bg-gray-50">
              <Columns3 class="h-4 w-4" />
            </button>
          </VDropdownImproved>
        </div>
      </div>

      <VDataTable
        :columns="columns"
        :items="filteredFunds"
        :loading="skeleton"
        :sortBy="[{ key: 'name', direction: 'asc' }]"
        empty-title="No Funds to display"
        empty-message="Try adjusting filters or search to see results."
      >
        <template #lifecycle_state="{ item }">
          <LifecycleBadge :status="item.lifecycle_state" />
        </template>

        <template #name="{ item }">
          <div class="min-w-0 max-w-full">
            <RouterLink
              v-if="!is_entity_link_disabled"
              class="block truncate font-semibold text-[#3b88af] hover:underline"
              :to="{ name: 'investing.entity-overview', params: { entity_id: item.id, entity_type: item.entity_type } }"
              :title="item.name"
            >
              {{ item.name }}{{ isEntityAdmin(item) ? ' (Admin)' : '' }}
            </RouterLink>
            <span v-else class="block truncate font-semibold text-gray-900" :title="item.name">
              {{ item.name }}{{ isEntityAdmin(item) ? ' (Admin)' : '' }}
            </span>
            <div
              v-if="!isEmpty(item.legal_name)"
              class="mt-0.5 truncate text-xs text-gray-500"
              :title="item.legal_name"
            >
              {{ item.legal_name }}
            </div>
            <VBadge v-if="item.entity_status" class="mt-1 w-fit" style="display: unset">In process</VBadge>
          </div>
        </template>

        <template #committed="{ item }">
          <div class="text-right">
            <div class="font-semibold text-gray-900">{{ n(item.insights?.committed, 'currency') }}</div>
            <div v-if="pctOfGoal(item)" class="mt-0.5 text-xs text-gray-500">{{ pctOfGoal(item) }}</div>
          </div>
        </template>

        <template #called="{ item }">
          <div class="text-right">
            <div class="font-semibold text-gray-900">{{ n(item.insights?.called_total, 'currency') }}</div>
            <div v-if="pctCalled(item)" class="mt-0.5 text-xs text-gray-500">{{ pctCalled(item) }}</div>
          </div>
        </template>

        <template #commitment_remaining="{ item }">
          <div class="text-right text-gray-900">{{ n(item.insights?.commitment_remaining, 'currency') }}</div>
        </template>

        <template #distributed="{ item }">
          <div class="text-right">
            <div class="font-semibold text-gray-900">{{ n(item.insights?.portfolio_distributed, 'currency') }}</div>
            <div v-if="moicLabel(item)" class="mt-0.5 text-xs text-gray-500">{{ moicLabel(item) }}</div>
          </div>
        </template>

        <template #cash_position="{ item }">
          <div class="text-right">
            <div class="font-semibold text-gray-900">{{ n(item.insights?.cash_position, 'currency') }}</div>
            <div v-if="pctDeployed(item)" class="mt-0.5 text-xs text-gray-500">{{ pctDeployed(item) }}</div>
          </div>
        </template>

        <template #distributed_carried_interest="{ item }">
          <div class="text-right text-gray-900">{{ n(item.insights?.distributed_carried_interest, 'currency') }}</div>
        </template>

        <template #admins="{ item }">
          <span v-for="(admin, idx) in item.admins ?? []" :key="admin.id">
            <RouterLink
              v-if="isAdmin"
              class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
              :to="{ name: 'investing.individual-overview', params: { individual_id: admin.id } }"
            >
              {{ admin.name }}
            </RouterLink>
            <span v-else class="text-gray-900">{{ admin.name }}</span>
            <span v-if="idx !== (item.admins?.length ?? 0) - 1">, </span>
          </span>
        </template>

        <template #selected_subscriptions="{ item }">
          <div class="flex flex-col px-3 text-left text-sm text-gray-600">
            <a
              v-for="sub in item.selected_subscriptions ?? []"
              :key="sub.name"
              class="mb-1 inline-block w-fit whitespace-nowrap rounded bg-white px-1.5 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-300"
              :href="`/${item.group?.subdomain}/billing/${item.entity_type}/${item.id}`"
            >
              <span class="pr-1.5">{{ sub.name }}</span>
              <span class="border-l border-gray-300 pl-1.5">{{ sub.status }}</span>
            </a>
          </div>
        </template>
      </VDataTable>
    </EntitiesCardTable>
  </IndividualLayout>
</template>
